// KpiRow.jsx — Task 11 artifact slot. Headline metrics with optional up/down delta.
// Props (from payload.template slot {type:'kpi_row', items:[…]}):
//   items: [{ label, value, format?, delta?, vs? }]
//   format ∈ number | currency_cents | percent | ratio  (client formats; server stores raw)
// Pure props→DOM. The RAW numeric value is preserved for number format (no locale commas under 1000)
// so the anti-fabrication gate can match the tile text against the payload value.
import React from "react"

// Format a stored raw value for display. Non-numbers pass through as their string.
export function formatValue(value, format) {
  if (typeof value !== "number") return String(value == null ? "" : value)
  switch (format) {
    case "currency_cents":
      return "$" + (value / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })
    case "percent":
      return value + "%"
    case "ratio":
      return value + "x"
    default:
      return value.toLocaleString()
  }
}

// A delta shown as a signed value. A fractional delta (|d| < 1) reads as a percent (e.g. -0.12 → -12%);
// an integer delta reads as a signed count (+3).
function formatDelta(delta) {
  if (typeof delta !== "number") return String(delta)
  if (Math.abs(delta) < 1 && delta !== 0) return (delta > 0 ? "+" : "") + Math.round(delta * 100) + "%"
  return (delta > 0 ? "+" : "") + delta
}

export default function KpiRow({ items, kpis }) {
  // Tolerate the reasonable key variance the live agent emits: the canonical key is `items`, but the
  // model also uses `kpis` (G-LIVE session 77). Render whichever non-empty array is present.
  const list = Array.isArray(items) && items.length ? items : Array.isArray(kpis) ? kpis : null
  if (!list || list.length === 0) return null
  return (
    <div className="afq-kpis" data-slot="kpi_row">
      {list.map((k, i) => {
        const dir = typeof k.delta === "number" ? (k.delta >= 0 ? "up" : "down") : null
        return (
          <div className="afq-kpi" key={i}>
            <div className="afq-kpi-l">{k.label}</div>
            <div className="afq-kpi-v">{formatValue(k.value, k.format)}</div>
            {k.delta != null && (
              <div className="afq-kpi-d">
                <span className={"afq-delta " + (dir || "")}>{formatDelta(k.delta)}</span>
                {k.vs ? <span className="afq-kpi-vs">{k.vs}</span> : null}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
